import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Shield, Circle } from 'lucide-react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useFetchPreventionShieldQuery, normalizePreventionShieldApiLanguage } from '#store';
import { CardShell } from './dashboardCardPrimitives';

/** Volt `$logo-green` (_variables.scss) */
const RING_PROGRESS = '#005C57';
const RING_TRACK = '#e8eaef';

/**
 * Pierścień postępu: pathLength + strokeDasharray w jednostkach 0–100
 * (pewniejsze niż pojedyncza liczba strokeDasharray w React/SVG).
 */
export function PreventionShieldCard() {
  const { t, i18n } = useTranslation();
  const userToken = useSelector((s) => s.auth.userToken);
  const shieldLanguage = useMemo(
    () => normalizePreventionShieldApiLanguage(i18n.language),
    [i18n.language],
  );
  const { data, isLoading, isError, isFetching, refetch } = useFetchPreventionShieldQuery(
    shieldLanguage,
    {
      skip: !userToken,
    },
  );

  const progress = useMemo(() => {
    const raw = data?.score;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    return Math.min(100, Math.max(0, n));
  }, [data?.score]);

  const missing = Array.isArray(data?.all_missing) ? data.all_missing.filter(Boolean) : [];

  const radius = 40;
  const strokeWidth = 8;
  const center = 56;

  const showRingLoading = isLoading && !data;
  const ringAria = showRingLoading
    ? t('dashboard.prevention-shield.loadingAria')
    : t('dashboard.prevention-shield.ringAria', { percent: Math.round(progress) });

  return (
    <CardShell className='shield-dashboard-card h-100 min-h-0 flex-1'>
      <div className='shrink-0 border-bottom bg-white px-4 py-3 pb-2'>
        <div
          className='mb-0 d-flex align-items-center gap-2 fw-semibold text-dark'
          style={{ fontSize: '1rem' }}
        >
          <Shield
            className='flex-shrink-0'
            style={{ width: '1.25rem', height: '1.25rem', color: 'var(--primary)' }}
            strokeWidth={2}
            aria-hidden
          />
          {t('dashboard.prevention-shield.title')}
          {isFetching && !isLoading ? (
            <Spinner animation='border' size='sm' className='ms-auto opacity-50' aria-hidden />
          ) : null}
        </div>
      </div>

      <div className='flex min-h-0 flex-1 flex-col px-4 pb-4 pt-3'>
        <div className='flex min-h-0 flex-1 flex-col'>
          <div className='flex min-h-0 flex-1 flex-col items-center justify-center'>
            <div className='shield-dashboard-ring-wrap flex w-full justify-center'>
              {showRingLoading ? (
                <div
                  className='d-flex align-items-center justify-content-center'
                  style={{ minHeight: 112 }}
                  role='status'
                  aria-live='polite'
                >
                  <Spinner animation='border' size='sm' className='text-muted' />
                </div>
              ) : (
                <svg
                  className='shield-dashboard-ring-svg shrink-0'
                  viewBox='0 0 112 112'
                  width={112}
                  height={112}
                  role='img'
                  aria-label={ringAria}
                >
                  <g transform={`rotate(-90 ${center} ${center})`}>
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      stroke={RING_TRACK}
                      strokeWidth={strokeWidth}
                      fill='none'
                    />
                    <circle
                      cx={center}
                      cy={center}
                      r={radius}
                      stroke={RING_PROGRESS}
                      strokeWidth={strokeWidth}
                      fill='none'
                      strokeLinecap='round'
                      pathLength='100'
                      strokeDasharray='100'
                      strokeDashoffset={100 - progress}
                      className='transition-[stroke-dashoffset] duration-500 ease-out'
                    />
                  </g>
                  <text
                    x={center}
                    y={center}
                    textAnchor='middle'
                    dominantBaseline='central'
                    className='shield-dashboard-percent-text'
                  >
                    {`${Math.round(progress)}%`}
                  </text>
                </svg>
              )}
            </div>
          </div>

          {isError ? (
            <div className='mt-2 text-center'>
              <p className='mb-2 text-xs text-muted-foreground'>
                {t('dashboard.prevention-shield.fetchError')}
              </p>
              <Button variant='outline-secondary' size='sm' type='button' onClick={() => refetch()}>
                {t('dashboard.prevention-shield.retry')}
              </Button>
            </div>
          ) : null}

          {!isError && missing.length > 0 ? (
            <div className='w-full shrink-0'>
              <p className='mb-3 mt-6 text-center text-sm font-medium text-gray-500'>
                {t('dashboard.prevention-shield.todoHeading')}
              </p>
              <div className='max-h-52 w-full overflow-y-auto'>
                <div className='shield-prevention-tasks mt-4'>
                  {missing.map((item, index) => (
                    <div key={index} className='shield-prevention-task-row'>
                      <span className='shield-prevention-task-icon' aria-hidden>
                        <Circle size={16} strokeWidth={2} />
                      </span>
                      <span className='shield-prevention-task-text'>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </CardShell>
  );
}
